import React, { useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import {
  CalendarClock, CheckCircle2, XCircle, CalendarX, FileText, ListChecks, ShieldCheck,
} from 'lucide-react';
import Button from '../../../components/ui/Button';

const Wrap = styled.div`display: flex; flex-direction: column; gap: 14px;`;

const Card = styled.div`
  background: #fff; border: 1px solid #E8ECF0;
  border-left: 4px solid ${({ $color }) => $color};
  border-radius: 12px; padding: 16px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04); display: flex; flex-direction: column; gap: 12px;
`;
const Head = styled.div`display: flex; align-items: center; gap: 12px;`;
const IconWrap = styled.div`
  width: 40px; height: 40px; border-radius: 10px; flex-shrink: 0;
  display: flex; align-items: center; justify-content: center;
  background: ${({ $bg }) => $bg}; color: ${({ $color }) => $color};
`;
const When = styled.div`
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: ${({ theme }) => theme.metrics.font.size.md};
  font-weight: ${({ theme }) => theme.metrics.font.weight.bold};
  color: ${({ theme }) => theme.colors.text.primary};
`;
const Meta = styled.div`
  display: flex; align-items: center; gap: 10px; flex-wrap: wrap; margin-top: 3px;
  font-family: ${({ theme }) => theme.metrics.font.family}; font-size: 12px;
  color: ${({ theme }) => theme.colors.text.dark.little};
`;
const Badge = styled.span`
  display: inline-flex; align-items: center; gap: 5px; padding: 2px 8px; border-radius: 999px;
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  background: ${({ $bg }) => $bg}; color: ${({ $color }) => $color};
`;
const Section = styled.div`
  font-family: ${({ theme }) => theme.metrics.font.family}; font-size: ${({ theme }) => theme.metrics.font.size.sm};
  color: ${({ theme }) => theme.colors.text.primary};
  span { display: flex; align-items: center; gap: 6px; font-size: 12px;
    color: ${({ theme }) => theme.colors.text.dark.little}; margin-bottom: 4px; font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold}; }
  div { white-space: pre-wrap; line-height: 1.5; }
`;
const Empty = styled.div`
  text-align: center; padding: 48px 16px; color: ${({ theme }) => theme.colors.text.dark.little};
  font-family: ${({ theme }) => theme.metrics.font.family}; font-size: ${({ theme }) => theme.metrics.font.size.sm};
`;

// Employee co-sign area on a conducted 1:1 — lightweight reflection + acknowledge
// so the meeting becomes a two-sided record (not a hard approval gate).
const AckBox = styled.div`
  background: rgba(2,145,239,0.05);
  border: 1px dashed #BFD9F2;
  border-radius: 10px;
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;
const AckLabel = styled.div`
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  color: ${({ theme }) => theme.colors.text.primary};
`;
const ReflectInput = styled.textarea`
  width: 100%;
  min-height: 56px;
  resize: vertical;
  padding: 8px 10px;
  border: 1px solid #cbd5e0;
  border-radius: 6px;
  font-family: inherit;
  font-size: 14px;
`;

const statusVisual = (status) => {
  switch (status) {
    case 'conducted': return { Icon: CheckCircle2, bg: 'rgba(2,211,191,0.12)', color: '#01A899' };
    case 'cancelled': return { Icon: XCircle, bg: 'rgba(102,102,119,0.10)', color: '#667' };
    case 'no_show': return { Icon: CalendarX, bg: 'rgba(234,88,12,0.10)', color: '#EA580C' };
    default: return { Icon: CalendarClock, bg: 'rgba(2,145,239,0.10)', color: '#0291EF' };
  }
};

const fmtDateTime = (iso, lang) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString(lang || undefined, {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
};

const parseActionItems = (json) => {
  if (!json) return [];
  try {
    const arr = JSON.parse(json);
    return Array.isArray(arr) ? arr : [];
  } catch { return []; }
};

const actionItemText = (it) => {
  if (typeof it === 'string') return it;
  if (it && typeof it === 'object') return it.title || it.text || it.description || JSON.stringify(it);
  return String(it);
};

function MeetingCard({ m, t, lang, onAcknowledge }) {
  const [reflection, setReflection] = useState('');
  const [busy, setBusy] = useState(false);
  const v = statusVisual(m.status);
  const actionItems = parseActionItems(m.actionItemsJson);
  const conductedUnacked = m.status === 'conducted' && !m.employeeAcknowledgedAt;

  return (
    <Card $color={v.color}>
      <Head>
        <IconWrap $bg={v.bg} $color={v.color}><v.Icon size={20} /></IconWrap>
        <div style={{ flex: 1, minWidth: 0 }}>
          <When>{fmtDateTime(m.scheduledAt, lang)}</When>
          <Meta>
            <Badge $bg="rgba(124,58,237,0.10)" $color="#7C3AED">
              {t(`onboardingOneOnOne.period.${m.period}`, { defaultValue: m.period })}
            </Badge>
            <Badge $bg={v.bg} $color={v.color}>
              {t(`onboardingOneOnOne.status.${m.status}`, { defaultValue: m.status })}
            </Badge>
            {m.conductedAt && (
              <span>{t('myOnboarding.oneOnOnes.conductedAt')}: {fmtDateTime(m.conductedAt, lang)}</span>
            )}
          </Meta>
        </div>
      </Head>

      {m.status === 'cancelled' && m.cancellationReason && (
        <Section>
          <span>{t('myOnboarding.oneOnOnes.cancellationReason')}</span>
          <div>{m.cancellationReason}</div>
        </Section>
      )}

      {m.managerNotesMarkdown && (
        <Section>
          <span><FileText size={13} />{t('myOnboarding.oneOnOnes.managerNotes')}</span>
          <div>{m.managerNotesMarkdown}</div>
        </Section>
      )}

      {actionItems.length > 0 && (
        <Section>
          <span><ListChecks size={13} />{t('myOnboarding.oneOnOnes.actionItems')}</span>
          <div>{actionItems.map((it) => `• ${actionItemText(it)}`).join('\n')}</div>
        </Section>
      )}

      {m.employeeAcknowledgedAt && (
        <Section>
          <span><ShieldCheck size={13} />{t('myOnboarding.oneOnOnes.youAcknowledged')} · {fmtDateTime(m.employeeAcknowledgedAt, lang)}</span>
          {m.employeeReflectionMarkdown && <div>{m.employeeReflectionMarkdown}</div>}
        </Section>
      )}

      {conductedUnacked && onAcknowledge && (
        <AckBox>
          <AckLabel>{t('myOnboarding.oneOnOnes.reflectionPrompt')}</AckLabel>
          <ReflectInput
            value={reflection}
            placeholder={t('myOnboarding.oneOnOnes.reflectionPlaceholder')}
            onChange={(e) => setReflection(e.target.value)}
          />
          <div>
            <Button
              disabled={busy}
              onClick={async () => { setBusy(true); await onAcknowledge(m.id, reflection.trim() || undefined); setBusy(false); }}
            >
              {t('myOnboarding.oneOnOnes.acknowledgeButton')}
            </Button>
          </div>
        </AckBox>
      )}
    </Card>
  );
}

export default function OneOnOnesTab({ oneOnOnes, onAcknowledge }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;

  const list = (Array.isArray(oneOnOnes) ? oneOnOnes : [])
    .slice()
    .sort((a, b) => new Date(a.scheduledAt).getTime() - new Date(b.scheduledAt).getTime());

  return (
    <Wrap>
      {list.length === 0 && <Empty>{t('myOnboarding.oneOnOnes.empty')}</Empty>}
      {list.map((m) => (
        <MeetingCard key={m.id} m={m} t={t} lang={lang} onAcknowledge={onAcknowledge} />
      ))}
    </Wrap>
  );
}
